import type {
  Flow360Case,
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
} from "../../flow360/types";
import type { ThreadAsset, ThreadRow } from "../thread-design-system";
import type { V3ProvenanceNode, V3WorkbenchData } from "../thread-v3/types";

export type FilamentPreviewCaseId = "nacelle" | "sweptwing";
export type ThreadFilamentArtifactKind = "geometry" | "surface_mesh" | "volume_mesh" | "case" | "demo";

export interface ThreadFilamentGeometryArtifact {
  id: string;
  name: string;
  kind: ThreadFilamentArtifactKind;
  kindLabel: string;
  sourceLabel: string;
  status?: string | null;
  updatedAt?: string | null;
  flow360ProjectId?: string | null;
  flow360GeometryId?: string | null;
  flow360SurfaceMeshId?: string | null;
  flow360VolumeMeshId?: string | null;
  flow360CaseId?: string | null;
  panelCount?: number | null;
  storageBytes?: number | null;
  provenanceNodeIds: string[];
  rowIds: string[];
  previewCaseId: FilamentPreviewCaseId;
}

const GEOMETRY_ID_KEYS = ["geom_flow360_geometry_id", "flow360_geometry_id", "geometry_id"];
const GEOMETRY_NAME_KEYS = ["geom_name", "flow360_geometry_name", "geometry_name", "design"];
const SURFACE_MESH_ID_KEYS = ["surf_mesh_flow360_surface_mesh_id", "flow360_surface_mesh_id", "surface_mesh_id", "surf_mesh_id"];
const SURFACE_MESH_NAME_KEYS = ["surf_mesh_name", "flow360_surface_mesh_name", "surface_mesh_name", "mesh"];
const VOLUME_MESH_ID_KEYS = ["vol_mesh_flow360_volume_mesh_id", "flow360_volume_mesh_id", "volume_mesh_id", "vol_mesh_id"];
const VOLUME_MESH_NAME_KEYS = ["vol_mesh_name", "flow360_volume_mesh_name", "volume_mesh_name", "mesh"];
const CASE_ID_KEYS = ["flow360_case_id", "case_id"];
const CASE_NAME_KEYS = ["flow360_case_name", "case_name", "name", "runId"];
const PROJECT_ID_KEYS = ["flow360_project_id", "project_id", "parentFolderId", "parent_folder_id"];
const STATUS_KEYS = ["status", "display_status", "visualization_status", "rootItemStatus", "root_item_status"];
const UPDATED_KEYS = ["updatedAt", "updated_at", "createdAt", "created_at"];
const STORAGE_KEYS = ["storageSize", "storage_size", "sizeBytes", "size_bytes", "bytes"];

const PANEL_COUNT_PATTERNS = [
  /surface.*(face|faces|cell|cells|panel|panels|triangle|triangles)/i,
  /(face|faces|cell|cells|panel|panels|triangle|triangles).*surface/i,
  /boundary.*(face|faces|cell|cells|panel|panels)/i,
  /n[_-]?(surface)?[_-]?(faces|panels|triangles|cells)/i,
  /num(ber)?[_-]?(surface)?[_-]?(faces|panels|triangles|cells)/i,
  /face[_-]?count/i,
  /panel[_-]?count/i,
  /total[_-]?(faces|panels|triangles)/i,
];

function stringValue(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string" && /^[0-9][0-9,._\s]*$/.test(value.trim())) {
    const parsed = Number(value.replace(/[,_\s]/g, ""));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

function firstString(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = stringValue(record[key]);
    if (value) return value;
  }
  return null;
}

function firstNumber(record: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = numberValue(record[key]);
    if (value != null) return value;
  }
  return null;
}

function matchesPanelCount(path: string): boolean {
  return PANEL_COUNT_PATTERNS.some((pattern) => pattern.test(path));
}

function largestPanelCount(source: unknown, path = "", depth = 0): number | null {
  if (source == null || depth > 7) return null;
  const direct = numberValue(source);
  if (direct != null && matchesPanelCount(path)) return direct;
  if (Array.isArray(source)) {
    let best = matchesPanelCount(path) && source.length > 0 ? source.length : null;
    for (const item of source.slice(0, 96)) {
      const nested = largestPanelCount(item, path, depth + 1);
      if (nested != null && (best == null || nested > best)) best = nested;
    }
    return best;
  }
  if (typeof source !== "object") return null;
  let best: number | null = null;
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    const nextPath = path ? `${path}.${key}` : key;
    const nested = largestPanelCount(value, nextPath, depth + 1);
    if (nested != null && (best == null || nested > best)) best = nested;
  }
  return best;
}

function kindLabel(kind: ThreadFilamentArtifactKind): string {
  if (kind === "surface_mesh") return "Surface mesh";
  if (kind === "volume_mesh") return "Volume mesh";
  if (kind === "case") return "Case";
  if (kind === "demo") return "Filament fixture";
  return "Geometry";
}

function previewCaseFor(label: string, fallback: FilamentPreviewCaseId = "sweptwing"): FilamentPreviewCaseId {
  return /(nacelle|duct|axisymmetric|pod)/i.test(label) ? "nacelle" : fallback;
}

function previewPanelCount(previewCaseId: FilamentPreviewCaseId): number {
  return previewCaseId === "nacelle" ? 480 : 2_370;
}

function canonicalId(artifact: Partial<ThreadFilamentGeometryArtifact>): string {
  if (artifact.kind === "demo" && artifact.id) return artifact.id;
  return (
    artifact.flow360SurfaceMeshId ||
    artifact.flow360GeometryId ||
    artifact.flow360VolumeMeshId ||
    artifact.flow360CaseId ||
    artifact.id ||
    artifact.name ||
    "thread-geometry"
  );
}

function addArtifact(
  artifacts: Map<string, ThreadFilamentGeometryArtifact>,
  artifact: Omit<ThreadFilamentGeometryArtifact, "kindLabel">,
) {
  const id = canonicalId(artifact);
  const existing = artifacts.get(id);
  if (!existing) {
    artifacts.set(id, { ...artifact, id, kindLabel: kindLabel(artifact.kind) });
    return;
  }
  const panelCount = Math.max(existing.panelCount ?? 0, artifact.panelCount ?? 0) || (existing.panelCount ?? artifact.panelCount);
  const storageBytes = Math.max(existing.storageBytes ?? 0, artifact.storageBytes ?? 0) || (existing.storageBytes ?? artifact.storageBytes);
  artifacts.set(id, {
    ...existing,
    name: existing.name || artifact.name,
    status: existing.status ?? artifact.status,
    updatedAt: existing.updatedAt ?? artifact.updatedAt,
    flow360ProjectId: existing.flow360ProjectId ?? artifact.flow360ProjectId,
    flow360GeometryId: existing.flow360GeometryId ?? artifact.flow360GeometryId,
    flow360SurfaceMeshId: existing.flow360SurfaceMeshId ?? artifact.flow360SurfaceMeshId,
    flow360VolumeMeshId: existing.flow360VolumeMeshId ?? artifact.flow360VolumeMeshId,
    flow360CaseId: existing.flow360CaseId ?? artifact.flow360CaseId,
    panelCount,
    storageBytes,
    provenanceNodeIds: Array.from(new Set([...existing.provenanceNodeIds, ...artifact.provenanceNodeIds])),
    rowIds: Array.from(new Set([...existing.rowIds, ...artifact.rowIds])),
    previewCaseId: existing.previewCaseId ?? artifact.previewCaseId,
  });
}

function addProvenanceNode(artifacts: Map<string, ThreadFilamentGeometryArtifact>, node: V3ProvenanceNode) {
  if (node.type !== "geometry" && node.type !== "mesh") return;
  const attrs = node.attrs ?? {};
  const name = firstString(attrs, [...GEOMETRY_NAME_KEYS, ...SURFACE_MESH_NAME_KEYS, "label", "name"]) ?? node.label;
  const surfaceMeshId = firstString(attrs, SURFACE_MESH_ID_KEYS);
  const previewCaseId = previewCaseFor(name);
  const hasFlow360Artifact = Boolean(surfaceMeshId || firstString(attrs, GEOMETRY_ID_KEYS) || firstString(attrs, VOLUME_MESH_ID_KEYS) || firstString(attrs, CASE_ID_KEYS));
  const kind: ThreadFilamentArtifactKind = surfaceMeshId || node.type === "mesh" ? "surface_mesh" : "geometry";
  addArtifact(artifacts, {
    id: surfaceMeshId ?? firstString(attrs, GEOMETRY_ID_KEYS) ?? node.id,
    name,
    kind,
    sourceLabel: "Thread provenance",
    status: firstString(attrs, STATUS_KEYS) ?? node.health ?? null,
    updatedAt: firstString(attrs, UPDATED_KEYS),
    flow360ProjectId: firstString(attrs, PROJECT_ID_KEYS),
    flow360GeometryId: firstString(attrs, GEOMETRY_ID_KEYS),
    flow360SurfaceMeshId: surfaceMeshId,
    flow360VolumeMeshId: firstString(attrs, VOLUME_MESH_ID_KEYS),
    flow360CaseId: firstString(attrs, CASE_ID_KEYS),
    panelCount: largestPanelCount(attrs) ?? (hasFlow360Artifact ? null : previewPanelCount(previewCaseId)),
    storageBytes: firstNumber(attrs, STORAGE_KEYS),
    provenanceNodeIds: [node.id],
    rowIds: [],
    previewCaseId,
  });
}

function addRunRow(artifacts: Map<string, ThreadFilamentGeometryArtifact>, row: ThreadRow) {
  const record = row as Record<string, unknown>;
  const geometryId = firstString(record, GEOMETRY_ID_KEYS);
  const surfaceMeshId = firstString(record, SURFACE_MESH_ID_KEYS);
  const volumeMeshId = firstString(record, VOLUME_MESH_ID_KEYS);
  const caseId = firstString(record, CASE_ID_KEYS);
  const rowName = firstString(record, [...SURFACE_MESH_NAME_KEYS, ...VOLUME_MESH_NAME_KEYS, ...GEOMETRY_NAME_KEYS, ...CASE_NAME_KEYS, "design", "mesh"]) ?? row.runId;
  const hasFlow360Artifact = Boolean(geometryId || surfaceMeshId || volumeMeshId || caseId);
  const hasLocalGeometry = Boolean(row.design || row.mesh || rowName !== row.runId);
  const previewCaseId = previewCaseFor(rowName);
  if (!hasFlow360Artifact && !hasLocalGeometry) return;
  addArtifact(artifacts, {
    id: surfaceMeshId ?? geometryId ?? volumeMeshId ?? caseId ?? `thread-row-geometry:${row.runId}`,
    name: rowName,
    kind: surfaceMeshId ? "surface_mesh" : geometryId ? "geometry" : volumeMeshId ? "volume_mesh" : caseId ? "case" : "geometry",
    sourceLabel: hasFlow360Artifact ? "Thread Flow360 artifact" : "Thread run table",
    status: firstString(record, STATUS_KEYS) ?? row.status ?? null,
    updatedAt: firstString(record, UPDATED_KEYS),
    flow360ProjectId: firstString(record, PROJECT_ID_KEYS),
    flow360GeometryId: geometryId,
    flow360SurfaceMeshId: surfaceMeshId,
    flow360VolumeMeshId: volumeMeshId,
    flow360CaseId: caseId,
    panelCount: largestPanelCount(record) ?? (hasFlow360Artifact ? null : previewPanelCount(previewCaseId)),
    storageBytes: firstNumber(record, STORAGE_KEYS),
    provenanceNodeIds: row.rowNodeId ? [row.rowNodeId] : [],
    rowIds: [row.runId],
    previewCaseId,
  });
}

function addAsset(artifacts: Map<string, ThreadFilamentGeometryArtifact>, asset: ThreadAsset) {
  const type = asset.type as string;
  const label = `${asset.label} ${asset.sub} ${asset.summary ?? ""}`;
  const isGeometryLike = /(geometry|surface.?mesh|volume.?mesh|filament|flow360)/i.test(`${type} ${label}`);
  if (!isGeometryLike) return;
  const record = {
    flow360_geometry_id: asset.external,
    name: asset.label,
  } as Record<string, unknown>;
  addArtifact(artifacts, {
    id: asset.external ?? asset.id,
    name: asset.label,
    kind: /surface.?mesh/i.test(label) ? "surface_mesh" : /volume.?mesh/i.test(label) ? "volume_mesh" : "geometry",
    sourceLabel: "Thread artifact",
    status: asset.health,
    updatedAt: asset.updated,
    flow360ProjectId: null,
    flow360GeometryId: firstString(record, GEOMETRY_ID_KEYS),
    flow360SurfaceMeshId: firstString(record, SURFACE_MESH_ID_KEYS),
    flow360VolumeMeshId: firstString(record, VOLUME_MESH_ID_KEYS),
    flow360CaseId: firstString(record, CASE_ID_KEYS),
    panelCount: largestPanelCount(asset.preview),
    storageBytes: null,
    provenanceNodeIds: asset.provenanceNodeId ? [asset.provenanceNodeId] : [],
    rowIds: asset.refs.rows ?? [],
    previewCaseId: previewCaseFor(label),
  });
}

function addLocalFilamentFixtures(artifacts: Map<string, ThreadFilamentGeometryArtifact>) {
  addArtifact(artifacts, {
    id: "thread-fixture-sweptwing-filament",
    name: "Swept wing panel wake fixture",
    kind: "demo",
    sourceLabel: "Local Thread fixture",
    status: "ready",
    updatedAt: null,
    flow360ProjectId: null,
    flow360GeometryId: null,
    flow360SurfaceMeshId: null,
    flow360VolumeMeshId: null,
    flow360CaseId: "appac-sweptwing-webgpu",
    panelCount: 2_370,
    storageBytes: null,
    provenanceNodeIds: [],
    rowIds: [],
    previewCaseId: "sweptwing",
  });
  addArtifact(artifacts, {
    id: "thread-fixture-nacelle-filament",
    name: "Nacelle panel wake fixture",
    kind: "demo",
    sourceLabel: "Local Thread fixture",
    status: "ready",
    updatedAt: null,
    flow360ProjectId: null,
    flow360GeometryId: null,
    flow360SurfaceMeshId: null,
    flow360VolumeMeshId: null,
    flow360CaseId: "appac-nacelle-webgpu",
    panelCount: 480,
    storageBytes: null,
    provenanceNodeIds: [],
    rowIds: [],
    previewCaseId: "nacelle",
  });
}

export function collectThreadFilamentGeometryArtifacts(data: V3WorkbenchData): ThreadFilamentGeometryArtifact[] {
  const artifacts = new Map<string, ThreadFilamentGeometryArtifact>();
  data.provenance.nodes.forEach((node) => addProvenanceNode(artifacts, node));
  data.rows.forEach((row) => addRunRow(artifacts, row));
  data.assets.forEach((asset) => addAsset(artifacts, asset));
  if (!artifacts.size && (data.source === "fixture" || data.source === "mock-fallback" || data.project?.id === "local-thread-project")) {
    addLocalFilamentFixtures(artifacts);
  }
  return Array.from(artifacts.values()).sort((left, right) => {
    const panelDelta = (right.panelCount ?? 0) - (left.panelCount ?? 0);
    if (panelDelta) return panelDelta;
    return left.name.localeCompare(right.name);
  });
}

export function artifactToFlow360Folder(artifact: ThreadFilamentGeometryArtifact | null): Flow360Folder | null {
  if (!artifact?.flow360ProjectId) return null;
  return {
    id: artifact.flow360ProjectId,
    name: "Thread-linked Flow360 project",
    type: "Project",
  };
}

export function artifactToFlow360Geometry(artifact: ThreadFilamentGeometryArtifact | null): Flow360Geometry | null {
  const id = artifact?.flow360GeometryId ?? (artifact?.kind === "geometry" ? artifact.id : null);
  if (!artifact || !id) return null;
  return {
    id,
    name: artifact.name,
    status: artifact.status,
    projectId: artifact.flow360ProjectId,
    storageSize: artifact.storageBytes ?? undefined,
    updatedAt: artifact.updatedAt ?? undefined,
  };
}

export function artifactToFlow360SurfaceMesh(artifact: ThreadFilamentGeometryArtifact | null): Flow360SurfaceMesh | null {
  if (!artifact) return null;
  const id = artifact.flow360SurfaceMeshId ?? (artifact.kind === "surface_mesh" || artifact.kind === "geometry" || artifact.kind === "demo" ? artifact.id : null);
  if (!id) return null;
  return {
    id,
    name: artifact.name,
    status: artifact.status,
    geometryId: artifact.flow360GeometryId,
    parentId: artifact.flow360GeometryId,
    projectId: artifact.flow360ProjectId,
    storageSize: artifact.storageBytes ?? undefined,
    updatedAt: artifact.updatedAt ?? undefined,
  };
}

export function artifactToFlow360VolumeMesh(artifact: ThreadFilamentGeometryArtifact | null): Flow360VolumeMesh | null {
  if (!artifact?.flow360VolumeMeshId && artifact?.kind !== "volume_mesh") return null;
  return {
    id: artifact.flow360VolumeMeshId ?? artifact.id,
    name: artifact.name,
    status: artifact.status,
    surfaceMeshId: artifact.flow360SurfaceMeshId,
    parentId: artifact.flow360SurfaceMeshId,
    projectId: artifact.flow360ProjectId,
    storageSize: artifact.storageBytes ?? undefined,
    updatedAt: artifact.updatedAt ?? undefined,
  };
}

export function artifactToFlow360Case(artifact: ThreadFilamentGeometryArtifact | null): Flow360Case | null {
  if (!artifact?.flow360CaseId && artifact?.kind !== "case") return null;
  return {
    id: artifact.flow360CaseId ?? artifact.id,
    name: artifact.name,
    status: artifact.status,
    volumeMeshId: artifact.flow360VolumeMeshId,
    parentId: artifact.flow360VolumeMeshId,
    projectId: artifact.flow360ProjectId,
    storageSize: artifact.storageBytes ?? undefined,
    updatedAt: artifact.updatedAt ?? undefined,
  };
}
